import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';

import { ErrorState } from '@/ui/ErrorState';
import { Spinner } from '@/ui/Spinner';
import { Card, CardContent, CardHeader, CardTitle } from '@/ui/base/card';
import { Input } from '@/ui/base/input';
import { Label } from '@/ui/base/label';
import { Switch } from '@/ui/base/switch';
import { Textarea } from '@/ui/base/textarea';
import { Button } from '@/ui/base/button';
import { toast } from 'sonner';
import { TEMPLATES_EMAIL_MOCK } from './dadosMock';

/**
 * Editor de template de e-mail (`/admin/emails/templates/:id`).
 *
 * O protótipo usa um editor rich-text (Tiptap). Aqui a paridade é funcional,
 * não de biblioteca: assunto, corpo, cor do título, ativo/inativo, chips de
 * variável e pré-visualização — sem introduzir dependência nova (CLAUDE.md,
 * "Stack"). Salvar só atualiza o estado local: não há API de e-mail ainda
 * (ONDA-CA1, ver dadosMock.ts).
 */
export function EmailTemplateEditorPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const templateOriginal = useMemo(() => TEMPLATES_EMAIL_MOCK.find((t) => t.id === id), [id]);

  const [assunto, setAssunto] = useState(templateOriginal?.assunto ?? '');
  const [corpo, setCorpo] = useState(templateOriginal?.corpo ?? '');
  const [corTitulo, setCorTitulo] = useState(templateOriginal?.corTitulo ?? '#1e3a5f');
  const [ativo, setAtivo] = useState(templateOriginal?.ativo ?? true);
  const [salvando, setSalvando] = useState(false);
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    const tempo = window.setTimeout(() => setCarregando(false), 200);
    return () => window.clearTimeout(tempo);
  }, []);

  if (carregando) {
    return <Spinner size="lg" className="min-h-[50vh]" />;
  }

  if (!templateOriginal) {
    return (
      <ErrorState
        variant="resource-not-found"
        title="Template não encontrado"
        description="Este template de e-mail não existe ou foi removido."
        primaryAction={{
          label: 'Voltar para e-mails',
          onClick: () => navigate('/admin/emails?tab=templates'),
        }}
      />
    );
  }

  function inserirVariavel(variavel: string) {
    setCorpo((atual) => `${atual}{{${variavel}}}`);
  }

  function salvar() {
    setSalvando(true);
    window.setTimeout(() => {
      setSalvando(false);
      toast.success('Template salvo (dado mockado — ainda não persiste em API real).');
    }, 400);
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/admin/emails?tab=templates')}
            aria-label="Voltar"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-xl font-bold text-foreground">{templateOriginal.nome}</h1>
            <p className="font-mono text-sm text-muted-foreground">{templateOriginal.tipo}</p>
          </div>
        </div>
        <Button onClick={salvar} disabled={salvando} className="gap-2">
          <Save className="h-4 w-4" />
          {salvando ? 'Salvando…' : 'Salvar'}
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-5">
        <div className="space-y-5 xl:col-span-2">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Configurações</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="assunto">Assunto do e-mail</Label>
                <Input
                  id="assunto"
                  value={assunto}
                  onChange={(e) => setAssunto(e.target.value)}
                  placeholder="Ex: Documento próximo do vencimento"
                />
                <p className="text-xs text-muted-foreground">
                  Use {'{{variavel}}'} para dados dinâmicos.
                </p>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label>Status</Label>
                  <p className="text-xs text-muted-foreground">
                    Templates inativos não serão enviados.
                  </p>
                </div>
                <Switch checked={ativo} onCheckedChange={setAtivo} aria-label="Template ativo" />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="cor-titulo">Cor do título</Label>
                <div className="flex items-center gap-3">
                  <input
                    id="cor-titulo"
                    type="color"
                    value={corTitulo}
                    onChange={(e) => setCorTitulo(e.target.value)}
                    className="h-10 w-10 cursor-pointer rounded-lg border"
                  />
                  <span className="font-mono text-sm text-muted-foreground">{corTitulo}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Conteúdo do e-mail</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div
                className="flex flex-wrap gap-1.5"
                role="group"
                aria-label="Variáveis disponíveis"
              >
                {templateOriginal.variaveisDisponiveis.map((v) => (
                  <Button
                    key={v}
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-auto rounded-full px-2.5 py-0.5 font-mono text-xs"
                    onClick={() => inserirVariavel(v)}
                  >
                    {'{{'}
                    {v}
                    {'}}'}
                  </Button>
                ))}
              </div>
              <Textarea
                value={corpo}
                onChange={(e) => setCorpo(e.target.value)}
                rows={12}
                aria-label="Corpo do e-mail"
                className="font-mono text-sm"
              />
            </CardContent>
          </Card>
        </div>

        <div className="xl:sticky xl:top-6 xl:col-span-3 xl:self-start">
          <Card>
            <CardContent className="p-5">
              <p className="mb-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Pré-visualização
              </p>
              <div className="overflow-hidden rounded-lg border">
                {/* Cor de fundo é a escolha do admin no seletor acima, não um token do design system — por isso vai inteira no style, contraste incluso. */}
                <div className="px-5 py-4" style={{ backgroundColor: corTitulo, color: '#ffffff' }}>
                  <p className="font-semibold">{assunto || 'Assunto do e-mail'}</p>
                </div>
                <div className="min-h-[200px] whitespace-pre-wrap bg-background px-5 py-4 text-sm text-foreground">
                  {corpo || <span className="text-muted-foreground">Corpo do e-mail</span>}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default EmailTemplateEditorPage;
